import {
  ActivityHold,
  ActivityTracker,
  activityTracker,
} from './activityTracker'
import { ActivitySubject } from './types'

/**
 * Fail-safe entry points into the tracker for UI code (AppButton, useTrackedAction, the confirm
 * modal). Tracking must never break the action itself: a tracker error is logged and the action
 * runs untracked. An error thrown by the action's own handler is passed on untouched.
 */

const warnTrackingError = (error: unknown) => {
  console.warn('[activity] tracking failed; the action runs untracked.', error)
}

/**
 * Runs `fn` as the action `name` on `subject` (see ActivityTracker.run): a new scope is opened
 * (the previous one ends), and a promise returned by `fn` keeps it open until it settles. A null
 * subject (no ActivitySubjectProvider) sends no headers. Returns what `fn` returns.
 */
export function runTrackedAction<T>(
  name: string,
  subject: ActivitySubject | null | undefined,
  fn: () => T,
  tracker: ActivityTracker = activityTracker
): T {
  let started = false
  try {
    return tracker.run(name, subject, () => {
      started = true
      return fn()
    })
  } catch (error) {
    if (started) {
      throw error
    }
    warnTrackingError(error)
    return fn()
  }
}

/** Holds the open scope (see ActivityTracker.hold). Null when none is open or on a tracker error. */
export function holdActivity(
  tracker: ActivityTracker = activityTracker
): ActivityHold | null {
  try {
    return tracker.hold()
  } catch (error) {
    warnTrackingError(error)
    return null
  }
}

/** Runs `fn` inside the held scope (or plainly, without a hold). Returns what `fn` returns. */
export function resumeInHold<T>(
  hold: ActivityHold | null | undefined,
  fn: () => T
): T {
  if (!hold) {
    return fn()
  }
  let started = false
  try {
    return hold.resume(() => {
      started = true
      return fn()
    })
  } catch (error) {
    if (started) {
      throw error
    }
    warnTrackingError(error)
    return fn()
  }
}

/** Lets a hold go (idempotent). */
export function releaseHold(hold: ActivityHold | null | undefined): void {
  if (!hold) {
    return
  }
  try {
    hold.release()
  } catch (error) {
    warnTrackingError(error)
  }
}
